import { filtersConfigTemplates, freeSearchConfig } from "../../../enums/filtersType";
import { evaluateFilter } from "../../../store/views/entityView";

const freeSearchText = (selections) => (selections?.[freeSearchConfig.param] ?? "").trim();
//
function buildFromDataOptions(dataConfig, courses, selectedId, meta = {}) {
    const pool = courses ?? [];
    const seen = new Map(); // id → { filterBy, value }

    pool.forEach((course) => {
        (dataConfig.courseKeys ?? []).forEach((keyConfig) => {
            const key = typeof keyConfig === "string" ? keyConfig : keyConfig.key;
            const filterBy = typeof keyConfig === "string"
                ? (dataConfig.filterBy ?? keyConfig)
                : (keyConfig.filterBy ?? dataConfig.filterBy ?? keyConfig.key);

            const raw = course?.[key];
            const values = Array.isArray(raw) ? raw : raw != null && raw !== "" ? [raw] : [];
            values.forEach((value) => {
                if (!seen.has(String(value))) seen.set(String(value), { filterBy, value });
            });
        });
    });

    const options = [...seen.entries()]
        .filter(([, entry]) => pool.some((c) => evaluateFilter(c, { by: entry.filterBy, value: entry.value })))
        .sort(([a], [b]) => a.localeCompare(b))
        .flatMap(([id, entry]) => {
            const item = (meta[dataConfig.labelFrom] ?? []).find((m) => m.name === id);
            const fallbackItem = !item && dataConfig.labelFallback
                ? (meta[dataConfig.labelFallback] ?? []).find((m) => m.name === id)
                : null;
            const all = (item ?? fallbackItem)?.text?.all;
            if (!all?.he && !all?.ar) return [];

            return [{ id, label: { he: all.he ?? id, ar: all.ar ?? id }, filters: [{ by: entry.filterBy, value: entry.value }] }];
        });

    if (selectedId && !options.some((o) => o.id === selectedId)) {
        const entry = seen.get(selectedId);
        if (entry) options.push({ id: selectedId, label: { he: selectedId, ar: selectedId }, filters: [{ by: entry.filterBy, value: entry.value }] });
    }

    return options;
}

function buildFromJsonOptions(jsonConfig, courses, selectedId) {
    const toFilter = (opt) => ({ by: opt.filterBy, value: opt.value, ...(opt.operator ? { operator: opt.operator } : {}) });
    const options = (jsonConfig.options ?? [])
        .filter((opt) => courses?.some((c) => evaluateFilter(c, toFilter(opt))))
        .map((opt) => ({ id: opt.id, label: opt.label ?? { he: opt.id, ar: opt.id }, filters: [toFilter(opt)] }));

    const selectedOpt = (jsonConfig.options ?? []).find((o) => o.id === selectedId);
    if (selectedOpt && !options.some((o) => o.id === selectedId)) {
        options.push({ id: selectedOpt.id, label: selectedOpt.label ?? { he: selectedOpt.id, ar: selectedOpt.id }, filters: [toFilter(selectedOpt)] });
    }
    return options;
}

export function buildOptionsForFilter(filterConfig, courses, selectedId, meta = {}) {
    if (filterConfig.fromData) return buildFromDataOptions(filterConfig.fromData, courses, selectedId, meta);
    if (filterConfig.fromJson) return buildFromJsonOptions(filterConfig.fromJson, courses, selectedId);
    return [];
}

export function buildFiltersConfigResolved(templates, courses, selections, meta = {}) {
    return (templates ?? []).map((filter) => ({
        name: filter.name,
        label: filter.label,
        options: buildOptionsForFilter(filter, courses, selections?.[filter.name], meta),
    }));
}

function resolveFilterForSelection(filterConfig, selectedId, courses) {
    if (!selectedId) return [];

    if (filterConfig.fromData) {
        const pool = courses ?? [];
        for (const course of pool) {
            for (const keyConfig of (filterConfig.fromData.courseKeys ?? [])) {
                const key = typeof keyConfig === "string" ? keyConfig : keyConfig.key;
                const filterBy = typeof keyConfig === "string"
                    ? (filterConfig.fromData.filterBy ?? keyConfig)
                    : (keyConfig.filterBy ?? filterConfig.fromData.filterBy ?? keyConfig.key);
                const raw = course?.[key];
                const values = Array.isArray(raw) ? raw : raw != null && raw !== "" ? [raw] : [];
                if (values.some((v) => String(v) === selectedId)) {
                    const value = values.find((v) => String(v) === selectedId);
                    return [{ by: filterBy, value }];
                }
            }
        }
        if (filterConfig.fromData.filterBy) return [{ by: filterConfig.fromData.filterBy, value: selectedId }];
        return [];
    }

    if (filterConfig.fromJson) {
        const opt = (filterConfig.fromJson.options ?? []).find((o) => o.id === selectedId);
        return opt ? [{ by: opt.filterBy, value: opt.value, ...(opt.operator ? { operator: opt.operator } : {}) }] : [];
    }

    return [];
}

export function parseSelectionsFromSearchParams(searchParams, templates = filtersConfigTemplates, defaults = {}) {
    return {
        ...Object.fromEntries(
            templates.map(({ name }) => [name, searchParams.get(name) ?? defaults[name] ?? null])
        ),
        [freeSearchConfig.param]: searchParams.get(freeSearchConfig.param) ?? "",
    };
}

export function resolveActiveFiltersFromSelections(selections, templates, courses) {
    const dropdown = (templates ?? []).flatMap((filter) =>
        resolveFilterForSelection(filter, selections?.[filter.name], courses)
    );
    return [...freeSearchConfig.filter(freeSearchText(selections)), ...dropdown];
}

/** URL → activeFilters for discovery results */
export function buildDiscoveryFilterState(searchParams, templates = filtersConfigTemplates, courses, defaults = {}) {
    const selections = parseSelectionsFromSearchParams(searchParams, templates, defaults);
    return resolveActiveFiltersFromSelections(selections, templates, courses);
}
